import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Upload, FileSpreadsheet, Mail, Send, CheckCircle2, ShieldCheck, Zap, DollarSign,
} from "lucide-react";
import { COMPANY, CATEGORIES, INVENTORY_CONDITIONS, PROCESS } from "../data/mock";
import { useToast } from "../hooks/use-toast";

const INITIAL = {
  name: "", company: "", email: "", phone: "",
  category: "", value: "", location: "", message: "",
  units: "", cases: "", pallets: "", expiration: "",
};

export default function SubmitInventory() {
  const [form, setForm] = useState(INITIAL);
  const [conditions, setConditions] = useState([]);
  const [fileName, setFileName] = useState("");
  const { toast } = useToast();

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleCondition = (c) =>
    setConditions((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setFileName(f.name);
  };

  const buildMailto = () => {
    const subject = encodeURIComponent(
      `Overstock Inventory Submission${form.company ? " \u2014 " + form.company : ""}`
    );
    const bodyLines = [
      "Hello, I'd like to submit inventory for sale.",
      "",
      `Name: ${form.name}`,
      `Company: ${form.company}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `Primary Category: ${form.category}`,
      `Condition(s): ${conditions.join(", ")}`,
      `Approx. Retail Value: ${form.value}`,
      `Total Units: ${form.units}`,
      `Total Cases: ${form.cases}`,
      `Total Pallets: ${form.pallets}`,
      `Expiration Date (if applicable): ${form.expiration}`,
      `Location: ${form.location}`,
      "",
      "Details:",
      form.message,
      "",
      fileName
        ? `(Please remember to attach my inventory sheet: ${fileName})`
        : "(I will attach my inventory sheet / spreadsheet to this email.)",
    ];
    const body = encodeURIComponent(bodyLines.join("\n"));
    return `mailto:${COMPANY.email}?subject=${subject}&body=${body}`;
  };

  const submit = (e) => {
    e.preventDefault();
    window.location.href = buildMailto();
    toast({
      title: "Opening your email...",
      description: "Please attach your inventory sheet and hit send. We'll respond quickly!",
    });
  };

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy-brand text-white">
        <div className="hero-blob pointer-events-none absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full" />
        <div className="mx-auto max-w-4xl px-5 py-16 text-center lg:py-20 lg:px-8">
          <span className="mb-5 inline-flex items-center gap-2 rounded-full bg-teal-brand/20 px-4 py-1.5 text-xs font-semibold text-teal-brand">
            <FileSpreadsheet size={14} /> Sellers Welcome
          </span>
          <h1 className="text-4xl font-extrabold sm:text-5xl">Submit Your Inventory to Us</h1>
          <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-slate-300">
            Turn your overstock, closeouts and excess merchandise into capital. Send us your inventory
            sheet and one of our buyers will review it and follow up with an offer — fast, discreet and
            hassle-free.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="#submit-form"
              className="rounded-full bg-teal-brand px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-105"
            >
              Submit Inventory for Sale
            </a>
            <a
              href={`mailto:${COMPANY.email}?subject=${encodeURIComponent("Overstock Inventory Submission")}`}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              <Mail size={16} /> Email {COMPANY.email}
            </a>
          </div>
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="border-b border-slate-100 bg-white">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 py-10 sm:grid-cols-3 lg:px-8">
          {[
            { icon: Zap, t: "Fast Review", d: "We evaluate submissions around the clock." },
            { icon: DollarSign, t: "Immediate Payment", d: "Paid in full via wire on receipt of goods." },
            { icon: ShieldCheck, t: "Discreet & Protected", d: "Your pricing and brand stay protected." },
          ].map((x) => {
            const Icon = x.icon;
            return (
              <div key={x.t} className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal-brand/10 text-teal-brand">
                  <Icon size={22} />
                </div>
                <div>
                  <p className="font-bold text-navy-brand">{x.t}</p>
                  <p className="text-sm text-slate-500">{x.d}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FORM */}
      <section id="submit-form" className="mx-auto max-w-5xl px-5 py-16 lg:px-8">
        <div className="mb-10 text-center">
          <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">Inventory Submission</span>
          <h2 className="mt-3 text-3xl font-extrabold text-navy-brand sm:text-4xl">Tell us what you have</h2>
          <p className="mx-auto mt-4 max-w-xl text-[15px] text-slate-500">
            Fill out the details below. When you submit, your email app opens with everything pre-filled —
            just attach your inventory sheet (Excel/CSV) and send.
          </p>
        </div>

        <form onSubmit={submit} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm lg:p-10">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Full Name" required value={form.name} onChange={update("name")} placeholder="John Smith" />
            <Field label="Company" value={form.company} onChange={update("company")} placeholder="Your company" />
            <Field label="Email" type="email" required value={form.email} onChange={update("email")} placeholder="you@company.com" />
            <Field label="Phone" value={form.phone} onChange={update("phone")} placeholder="(000) 000-0000" />
          </div>

          {/* Category + value */}
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-navy-brand">Primary Category</label>
              <select
                value={form.category}
                onChange={update("category")}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy-brand outline-none transition-colors focus:border-teal-brand"
              >
                <option value="">Select a category</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
                <option value="Other">Other</option>
              </select>
            </div>
            <Field label="Approx. Retail Value" value={form.value} onChange={update("value")} placeholder="$ e.g. 250,000" />
          </div>

          {/* Quantities */}
          <div className="mt-5 grid gap-5 sm:grid-cols-3">
            <Field label="How many units?" value={form.units} onChange={update("units")} placeholder="e.g. 12,000" />
            <Field label="How many cases?" value={form.cases} onChange={update("cases")} placeholder="e.g. 500" />
            <Field label="How many pallets?" value={form.pallets} onChange={update("pallets")} placeholder="e.g. 24" />
          </div>

          {/* Expiration */}
          <div className="mt-5">
            <label className="mb-1.5 block text-sm font-semibold text-navy-brand">
              Expiration date <span className="font-normal text-slate-400">(if applicable)</span>
            </label>
            <input
              type="date"
              value={form.expiration}
              onChange={update("expiration")}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy-brand outline-none transition-colors focus:border-teal-brand"
            />
          </div>

          {/* Conditions */}
          <div className="mt-6">
            <label className="mb-2.5 block text-sm font-semibold text-navy-brand">Condition of Inventory</label>
            <div className="flex flex-wrap gap-2.5">
              {INVENTORY_CONDITIONS.map((c) => {
                const active = conditions.includes(c);
                return (
                  <button
                    type="button"
                    key={c}
                    onClick={() => toggleCondition(c)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 ${
                      active
                        ? "border-teal-brand bg-teal-brand text-white"
                        : "border-slate-200 bg-white text-navy-brand hover:border-teal-brand"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5">
            <Field label="Inventory Location" value={form.location} onChange={update("location")} placeholder="City, State" />
          </div>

          {/* Upload */}
          <div className="mt-5">
            <label className="mb-1.5 block text-sm font-semibold text-navy-brand">Inventory Sheet (Excel / CSV)</label>
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center transition-colors hover:border-teal-brand hover:bg-teal-brand/5">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-brand/10 text-teal-brand">
                <Upload size={22} />
              </div>
              {fileName ? (
                <p className="flex items-center gap-2 text-sm font-medium text-navy-brand">
                  <CheckCircle2 size={16} className="text-teal-brand" /> {fileName}
                </p>
              ) : (
                <>
                  <p className="text-sm font-medium text-navy-brand">Choose your inventory file</p>
                  <p className="text-xs text-slate-500">.xls, .xlsx, .csv — you'll attach it to the email on submit</p>
                </>
              )}
              <input type="file" accept=".xls,.xlsx,.csv" className="hidden" onChange={onFile} />
            </label>
          </div>

          <div className="mt-5">
            <label className="mb-1.5 block text-sm font-semibold text-navy-brand">Additional Details</label>
            <textarea
              value={form.message}
              onChange={update("message")}
              rows={4}
              placeholder="Quantity / pallets, brands, packaging, timeline, etc."
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-teal-brand"
            />
          </div>

          <button
            type="submit"
            className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy-brand px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-teal-brand sm:w-auto"
          >
            Submit My Inventory <Send size={16} />
          </button>
          <p className="mt-3 text-xs text-slate-400">
            Submitting opens your email to {COMPANY.email} with the details pre-filled. Attach your sheet and send.
          </p>
        </form>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-extrabold text-navy-brand sm:text-4xl">What happens next</h2>
            <p className="mt-4 text-[15px] text-slate-500">From submission to same-week payment in five simple steps.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3 lg:grid-cols-5">
            {PROCESS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.n} className="relative rounded-2xl bg-white p-6 text-center shadow-sm">
                  <span className="absolute right-4 top-3 text-4xl font-extrabold text-slate-100" style={{ fontFamily: "Sora" }}>{p.n}</span>
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-brand/10 text-teal-brand">
                    <Icon size={26} />
                  </div>
                  <h3 className="mb-2 text-[15px] font-bold text-navy-brand">{p.title}</h3>
                  <p className="text-xs leading-relaxed text-slate-500">{p.text}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link to="/how-it-works" className="inline-flex items-center gap-2 rounded-full border-2 border-teal-brand px-6 py-2.5 text-sm font-semibold text-teal-brand transition-all hover:bg-teal-brand hover:text-white">
              See full process
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, type = "text", required, value, onChange, placeholder }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-navy-brand">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-teal-brand"
      />
    </div>
  );
}
